import { Component, forwardRef, Input } from '@angular/core';
import { FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-text-input',
  templateUrl: './text-input.component.html',
  styleUrls: ['./text-input.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => TextInputComponent),
    multi: true
  }]
})

export class TextInputComponent{
  /**
   * `id` único do input, para o rótulo externo (`.ti-description`) poder usar
   * `for`. No preset `filter-styled` o `<mat-label>` nativo do Material fica
   * oculto e o rótulo visível é aquele `<label>` — sem `for`, o campo aparece
   * como "No label associated with a form field" no painel de Issues.
   */
  readonly inputId = `ti-input-${++TextInputComponent.instanceCount}`;
  private static instanceCount = 0;

  @Input() label: string;
  @Input() icon: string;
  @Input() name: string;
  @Input() align: string;
  @Input() hint: string;
  @Input() prefix: string;
  @Input() suffix: string;
  @Input() required: boolean;
  @Input() maxLength: number;
  @Input() readOnly: boolean;
  @Input() appearance: string;
  @Input() placeholder: string;
  @Input() autocomplete: string;
  @Input() formGroupController: FormGroup;
  @Input() warning: boolean;
  /** Rótulo descritivo ACIMA da caixa — mesma API/visual do
   *  `app-datetimepicker-input.description` / `app-number-input.description`.
   *  Usado pelo preset `.filter-styled` pra renderizar o label estrutural
   *  (não o mat-label flutuante interno do Material). Aceita chave i18n. */
  @Input() description?: string;
  /** Quando setado, renderiza um botão de "copiar" como matSuffix que copia
   *  o valor inteiro (passado aqui) pro clipboard. Útil quando o input tem
   *  `[prefix]` (ex.: URL-base) e o usuário precisa copiar prefix+input
   *  como string única — selecionar nativamente atravessando elementos
   *  separados não é trivial. */
  @Input() copyValue?: string;
  /** Quando setado, o trecho `fixedPrefix` do início do input fica
   *  PROTEGIDO contra edição (via FixedPrefixDirective) — usuário pode
   *  selecionar/copiar qualquer parte do valor, mas só edita após o
   *  prefix. Form control armazena o VALOR COMPLETO (prefix + slug). */
  @Input() fixedPrefix?: string;

  /** True quando o async validator do FormControl está em `PENDING` (ex.:
   *  URL checando uniqueness no backend). Lê do `formGroupController` —
   *  template usa pra renderizar spinner no matSuffix. */
  get asyncValidating(): boolean {
    return this.formGroupController?.get(this.name)?.status === 'PENDING';
  }

  /** Flag temporária pro feedback visual após copiar pro clipboard —
   *  troca o ícone do botão de `content_copy` pra `check` por ~1.4s. */
  copied = false;
  private copiedTimer?: any;

  onCopySuccess(): void {
    this.copied = true;
    if (this.copiedTimer) { clearTimeout(this.copiedTimer); }
    this.copiedTimer = setTimeout(() => { this.copied = false; }, 1400);
  }

  @Input() help: {
    label: string,
    align: 'start' | 'end'
  };

  checkValidity() {
    if (!!this.formGroupController.controls[this.name].invalid && !!this.warning) {
      return 'warning';
    }
  }
}
